import { useCallback, useEffect, useMemo, FC } from "react";
import { Box, Button } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { ArrowForwardIos } from "@mui/icons-material";
import { NewLearningRequestStep } from "utils/types";
import { useIntroStepStyles } from "./style";
import { useTranslation } from "react-i18next";
import i18n from "utils/i18n";
import { SelectionRadioGroup } from "./selection-radio-group";

interface NewLearningStepProps {
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  isMobile: boolean;
  courseSelection: boolean | null;
  setCourseSelection: (value: boolean | null) => void;
  setDialogTitle?: (title: string) => void;
}

export const NewLearning: FC<NewLearningStepProps> = ({
  onNext,
  isMobile,
  courseSelection,
  setCourseSelection,
  setDialogTitle,
}) => {
  const { t } = useTranslation();

  const classes = useIntroStepStyles({ isMobile });

  useEffect(() => {
    setDialogTitle?.(t("learning_request"));
  }, [setDialogTitle, t]);

  const isSelectionMade = useMemo(() => courseSelection !== null, [courseSelection]);

  const handleNext = useCallback(() => {
    if (isSelectionMade) {
      if (courseSelection) {
        onNext(NewLearningRequestStep.CourseDetails);
      } else {
        onNext(NewLearningRequestStep.LearningAnalysis);
      }
    }
  }, [isSelectionMade, courseSelection, onNext]);

  const handleSelection = useCallback(
    (value: boolean) => {
      setCourseSelection(value);
    },
    [setCourseSelection],
  );

  return (
    <Box className={classes.container}>
      {i18n.exists("new_learning_request_note") && (
        <Box className={classes.highlightNote} role="note">
          <InfoIcon color="primary" fontSize="small" />
          <p> {t("new_learning_request_note")}</p>
        </Box>
      )}
      <Box className={classes.sectionTitle}>{t("course_selection")}</Box>
      <SelectionRadioGroup
        value={courseSelection === null ? "" : String(courseSelection)}
        onChange={(value) => handleSelection(value === "true")}
        ariaLabel={t("course_selection_question")}
        styles={classes}
        options={[
          {
            value: "true",
            title: t("course_amendment"),
            description: t("course_amendment_description"),
          },
          {
            value: "false",
            title: t("learning_request"),
            description: t("new_learning_request_description"),
          },
        ]}
      />
      {isSelectionMade && (
        <Box className={classes.actionButtonContainer}>
          <Button
            type="button"
            variant="contained"
            onClick={handleNext}
            className={classes.primaryButton}
            endIcon={<ArrowForwardIos />}
          >
            {t("next")}
          </Button>
        </Box>
      )}
    </Box>
  );
};
